import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone } from "lucide-react";

export const SlittingRewindingMachine = () => {
  return (
    <>
      <Helmet>
        <title>Slitting Rewinding Machine Manufacturer in India - Hero Impex | German Engineering</title>
        <meta name="description" content="Leading slitting rewinding machine manufacturer in India. High-precision slitter rewinders for BOPP, polyester, paper. German engineering, 45+ years expertise, widths up to 2000mm. Get free quote!" />
        <meta name="keywords" content="slitting rewinding machine manufacturer India, slitter rewinder manufacturer, BOPP slitting machine, film slitting equipment, paper slitting machine India" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-amber-500">India's Premier Slitting Machine Manufacturer Since 1977</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Slitting Rewinding Machine Manufacturer in India
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Precision-engineered slitting rewinding machines for BOPP, polyester, paper, and flexible packaging films. German engineering standards with speeds up to 300 m/min and customizable widths from 500mm to 2000mm. Trusted by 800+ manufacturers across 20+ countries.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8">
                    Get FREE Technical Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+919884012741">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Phone className="w-5 h-5 mr-2" />
                    Expert Consultation: +91-9884012741
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Hero Impex is India's Most Trusted Slitting Rewinding Machine Manufacturer</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Hero Impex India Private Limited has been at the forefront of manufacturing high-precision slitting rewinding machines since 1977. As one of India's leading slitting rewinding machine manufacturers, we specialize in providing cutting-edge slitter rewinder solutions for the converting industry, flexible packaging manufacturers, label producers, and film processors across India and globally.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Our slitting rewinding machines are meticulously engineered to deliver maximum efficiency, minimal material waste, and superior edge quality. With German engineering principles and over 45 years of manufacturing expertise, we have successfully installed 5000+ slitting rewinding machines in India, UAE, Bangladesh, Saudi Arabia, USA, and 20+ other countries. Every slitting rewinding machine from Hero Impex is designed to handle diverse materials including BOPP film, PET film, polyester, paper, aluminum foil, and specialty laminates.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Whether you're a small converting unit looking for your first slitting rewinding machine or a large packaging manufacturer requiring high-speed slitter rewinders with advanced automation, Hero Impex delivers customized solutions that match your production requirements and budget. Our slitting rewinding machines are known for precision slitting, consistent tension control, and reliable performance even in the most demanding production environments.
              </p>
            </div>

            {/* Why Choose Hero Impex */}
            <div className="mb-12 bg-amber-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Hero Impex Slitting Rewinding Machines Over Other Manufacturers?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">German Engineering & Indian Value</h4>
                    <p className="text-slate-600">German pattern construction with precision-machined components, delivered at competitive Indian pricing. Our slitting rewinding machines follow stringent quality standards.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">High-Speed Precision Slitting</h4>
                    <p className="text-slate-600">Achieve speeds up to 300 m/min with razor blade or rotary knife slitting systems. Perfect slit edges with minimal burr formation.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Customizable Width Options</h4>
                    <p className="text-slate-600">Slitting rewinding machines available from 500mm to 2000mm working width. Custom sizes manufactured as per your material specifications.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Advanced Tension Control Systems</h4>
                    <p className="text-slate-600">Automatic tension control with load cells ensures consistent winding density and prevents material stretching or wrinkling.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Minimal Material Waste</h4>
                    <p className="text-slate-600">Precision slitting with automatic edge trim collection system maximizes your material yield and reduces production costs.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Complete After-Sales Support</h4>
                    <p className="text-slate-600">Pan-India service network with trained technicians. Spare parts availability with 24-hour delivery for critical components.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Slitting Rewinding Machine Technical Specifications</h2>
              <p className="text-lg text-slate-700 mb-6">
                Our slitting rewinding machines are available in various configurations to handle different materials, widths, and production speeds. Below are the standard specifications for our popular slitter rewinder models:
              </p>
              <Card className="border-2 border-amber-200">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Machine Parameters</h4>
                      <ul className="space-y-2">
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Slitting Width Range:</span>
                          <span className="font-semibold text-slate-900">500mm - 2000mm (Custom available)</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Maximum Speed:</span>
                          <span className="font-semibold text-slate-900">Up to 300 m/min</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Unwind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 1200mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Rewind Diameter:</span>
                          <span className="font-semibold text-slate-900">Max 1000mm</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Core Diameter:</span>
                          <span className="font-semibold text-slate-900">3" (76mm) / 6" (152mm)</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Material Thickness:</span>
                          <span className="font-semibold text-slate-900">12 micron - 300 micron</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Slitting Method:</span>
                          <span className="font-semibold text-slate-900">Razor Blade / Rotary Knife</span>
                        </li>
                        <li className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Number of Slits:</span>
                          <span className="font-semibold text-slate-900">Up to 20 slits (varies by width)</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3">Standard Features</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">PLC control system with HMI touchscreen interface</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Automatic tension control with load cell sensors</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Edge Position Control (EPC) system for web guiding</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Pneumatic shaft system for quick roll loading/unloading</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Digital length counter and preset stop function</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Automatic knife positioning system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Safety guards and emergency stop systems</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <span className="text-slate-700">Variable frequency drive (VFD) for speed control</span>
                        </li>
                      </ul>
                      <h4 className="font-bold text-slate-900 mt-6 mb-3">Optional Add-ons</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <span className="text-slate-700">Turret type automatic rewinding system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <span className="text-slate-700">Web cleaning and static elimination system</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <span className="text-slate-700">Online inspection system for quality control</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <span className="text-slate-700">Automatic core loading and ejection system</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image Gallery */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Hero Impex Slitting Rewinding Machines - Real Product Gallery</h2>
              <p className="text-lg text-slate-700 mb-6">
                View our precision-engineered slitting rewinding machines manufactured at our state-of-the-art facility in Chennai, India. These machines are currently operational in 20+ countries worldwide.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/slitting/bopp-adhasive-tape-slitting-rewinding-machine.jpg" 
                    alt="BOPP Tape Slitting Rewinding Machine - Hero Impex India Manufacturer"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">BOPP Tape Slitting Machine</h4>
                    <p className="text-sm text-slate-600">Complete slitting rewinding system for BOPP adhesive tapes. Features automatic tension control and precision slitting up to 1650mm width.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/slitting/automatic-slitting-machine-.jpg" 
                    alt="Automatic Slitting Machine Manufacturer India - Hero Impex Chennai"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Automatic Slitting System</h4>
                    <p className="text-sm text-slate-600">Fully automated slitting machine with PLC control, touchscreen HMI, and automatic knife positioning system for maximum efficiency.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all overflow-hidden">
                  <img 
                    src="/images/products/slitting/turret-type-slitting-rewinding-machine-(2).jpg" 
                    alt="Turret Type Slitting Rewinding Machine - Hero Impex India"
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Turret Slitting Rewinder</h4>
                    <p className="text-sm text-slate-600">Advanced turret type rewinding for non-stop operation. Ideal for high-volume production with quick roll changeover.</p>
                  </CardContent>
                </Card>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-blue-100 hover:shadow-lg transition-all overflow-hidden">
                  <img 
                    src="/images/products/slitting/manual-slicer.jpg" 
                    alt="Manual Tape Slicer Machine - Hero Impex Manufacturing"
                    className="w-full h-48 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Manual Slicing Machine</h4>
                    <p className="text-sm text-slate-600">Cost-effective manual slicer for small-scale operations. Perfect for businesses starting tape production.</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-100 hover:shadow-lg transition-all overflow-hidden">
                  <img 
                    src="/images/products/slitting/slitting-machine-1.jpg" 
                    alt="Industrial Slitting Machine India - Hero Impex Manufacturer"
                    className="w-full h-48 object-cover"
                  />
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Industrial Slitting Line</h4>
                    <p className="text-sm text-slate-600">Heavy-duty industrial slitting line for high-volume converting operations with speeds up to 300 m/min.</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Applications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Materials Compatible with Our Slitting Rewinding Machines</h2>
              <p className="text-lg text-slate-700 mb-6">
                Hero Impex slitting rewinding machines are versatile and can process a wide range of flexible packaging materials and specialty films. Our slitter rewinders are used across multiple industries:
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Plastic Films</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• BOPP Film (Biaxially Oriented Polypropylene)</li>
                      <li>• PET Film / Polyester Film</li>
                      <li>• PE Film (Polyethylene)</li>
                      <li>• PVC Film (Polyvinyl Chloride)</li>
                      <li>• CPP Film (Cast Polypropylene)</li>
                      <li>• Stretch Film & Shrink Film</li>
                      <li>• Metalized Films</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Paper & Specialty Materials</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Kraft Paper & Coated Paper</li>
                      <li>• Thermal Paper Rolls</li>
                      <li>• Release Liner Paper</li>
                      <li>• Label Stock Paper</li>
                      <li>• Cardboard & Paperboard</li>
                      <li>• Wax Paper & Baking Paper</li>
                      <li>• Medical Grade Papers</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-100 hover:border-amber-400 transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Laminates & Foils</h4>
                    <ul className="space-y-1 text-slate-600">
                      <li>• Aluminum Foil</li>
                      <li>• Laminated Films (Multi-layer)</li>
                      <li>• Adhesive Tapes (Pre-coated)</li>
                      <li>• Foam Tapes & Double-Sided Tapes</li>
                      <li>• Protective Films</li>
                      <li>• Holographic Films</li>
                      <li>• Medical & Pharmaceutical Films</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Industries Served */}
            <div className="mb-12 bg-slate-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Industries We Serve with Our Slitting Rewinding Machines</h2>
              <p className="text-lg text-slate-700 mb-6">
                Our slitting rewinding machines are trusted by manufacturers across diverse industries in India and internationally:
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                {['Flexible Packaging Industry', 'Label Manufacturing', 'Converting & Lamination', 'Adhesive Tape Production', 'Printing Industry', 'Paper Mills & Converters', 'Film Extrusion Units', 'Pharmaceutical Packaging', 'Food Packaging', 'E-commerce Packaging', 'Medical Device Packaging', 'Textile Industry'].map((industry) => (
                  <div key={industry} className="bg-white rounded-lg p-4 text-center border-2 border-amber-100 hover:border-amber-400 transition-all">
                    <span className="font-semibold text-slate-900">{industry}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Benefits */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Key Benefits of Hero Impex Slitting Rewinding Machines</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-l-4 border-amber-500">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Superior Edge Quality</h4>
                    <p className="text-slate-700">Our precision slitting systems (razor blade or rotary knife) deliver clean, burr-free edges that are critical for high-quality flexible packaging and label production. Perfect slitting reduces material rejection and improves downstream processing.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-amber-500">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Energy Efficient Operation</h4>
                    <p className="text-slate-700">Variable frequency drives and optimized motor selection ensure minimal power consumption. Our slitting rewinding machines consume 30-40% less energy compared to older generation slitter rewinders, reducing your operational costs significantly.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-amber-500">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Easy Operator Training</h4>
                    <p className="text-slate-700">User-friendly HMI touchscreen interface with graphical displays makes operation simple. We provide comprehensive training to your operators during installation, ensuring quick production ramp-up with minimal learning curve.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-amber-500">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Low Maintenance Design</h4>
                    <p className="text-slate-700">Robust construction with high-quality bearings, precision-ground shafts, and minimal moving parts ensures long service life. Regular maintenance requirements are simple and can be performed by your in-house team with our guidance.</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Internal Linking Section */}
            <div className="mb-12 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Complete Solutions for Tape & Film Manufacturing</h2>
              <p className="text-lg text-slate-700 mb-6">
                Looking for a complete tape or film production line? Hero Impex offers integrated solutions:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">BOPP Tape Manufacturing</h4>
                    <p className="text-slate-700 mb-4">Combine our slitting rewinding machine with a <Link to="/bopp-tape-coating-machine-manufacturer-india" className="text-amber-600 hover:text-amber-700 font-semibold">BOPP tape coating machine</Link> for complete BOPP tape production. We supply both machines perfectly matched for seamless integration.</p>
                    <Link to="/bopp-tape-coating-machine-manufacturer-india">
                      <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                        View BOPP Coating Machines →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Adhesive Coating Lines</h4>
                    <p className="text-slate-700 mb-4">Pair your slitting rewinding machine with our <Link to="/adhesive-coating-machine-manufacturer-india" className="text-amber-600 hover:text-amber-700 font-semibold">adhesive coating machines</Link> for specialty tape production, label manufacturing, and lamination applications.</p>
                    <Link to="/adhesive-coating-machine-manufacturer-india">
                      <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                        Explore Coating Solutions →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Complete Plant Setup</h4>
                    <p className="text-slate-700 mb-4">Planning to start a tape manufacturing business? Explore our <Link to="/complete-tape-manufacturing-plant-setup-india" className="text-amber-600 hover:text-amber-700 font-semibold">turnkey tape plant solutions</Link> with coating, slitting, and packaging equipment—all from one supplier.</p>
                    <Link to="/complete-tape-manufacturing-plant-setup-india">
                      <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                        Turnkey Plant Solutions →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-3">All Tape Making Equipment</h4>
                    <p className="text-slate-700 mb-4">As a comprehensive <Link to="/tape-making-machine-supplier-india" className="text-amber-600 hover:text-amber-700 font-semibold">tape making machine supplier</Link>, we provide coating machines, slitting machines, printing units, and packaging equipment—complete solutions under one roof.</p>
                    <Link to="/tape-making-machine-supplier-india">
                      <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                        View All Equipment →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* FAQs */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Frequently Asked Questions About Slitting Rewinding Machines</h2>
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">What is the difference between razor blade slitting and rotary knife slitting?</h4>
                    <p className="text-slate-700">Razor blade slitting uses thin blades to shear the material, ideal for thinner films (12-80 micron) like BOPP and PET. Rotary knife slitting uses circular knives with an anvil roller, suitable for thicker materials (80-300 micron) like paper, cardboard, and foam tapes. We help you choose the right method based on your material specifications.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">What is the typical delivery time for a slitting rewinding machine?</h4>
                    <p className="text-slate-700">Standard models: 60-75 days from order confirmation. Custom machines with specific width or automation requirements: 90-120 days. We maintain critical components in stock to ensure faster delivery for urgent projects.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">Can I upgrade my existing slitting machine with automation features?</h4>
                    <p className="text-slate-700">Yes, we offer retrofit solutions to upgrade older slitting rewinding machines with PLC control, automatic tension control, and EPC systems. Our technical team can assess your existing machine and recommend cost-effective upgrades to improve productivity.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">What is the power requirement for a typical slitting rewinding machine?</h4>
                    <p className="text-slate-700">Power consumption varies by machine width and speed. For a 1000mm width machine operating at 150 m/min, typical power requirement is 15-25 KW. Larger machines (1600-2000mm) require 30-50 KW. We provide detailed electrical specifications with every quotation.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">Do you provide training and installation support?</h4>
                    <p className="text-slate-700">Absolutely! Every slitting rewinding machine comes with complete installation support. Our technical team will install, commission, and train your operators at your facility. We also provide operation manuals and video training materials for reference.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h4 className="font-bold text-slate-900 mb-2">What spare parts should I keep in stock for my slitting machine?</h4>
                    <p className="text-slate-700">We recommend keeping razor blades (if applicable), spare pneumatic seals, drive belts, and basic electrical components. We provide a recommended spare parts list with every machine. Critical spares are available from our Chennai warehouse with 24-hour delivery across India.</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Upgrade Your Slitting Operations?</h2>
              <p className="text-xl mb-6 text-amber-50">
                Get a FREE technical consultation and detailed quotation for your specific slitting rewinding requirements. Our engineers will help you select the perfect slitter rewinder configuration based on your material, width, speed, and budget requirements.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 text-lg px-8">
                    Request FREE Quotation & Machine Demo
                  </Button>
                </Link>
                <a href="https://wa.me/919884012741?text=I need information about slitting rewinding machines. Please send specifications and pricing." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                    WhatsApp Inquiry: +91-9884012741
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-amber-100">
                ✓ German Engineering Standards • ✓ 45+ Years Manufacturing Expertise • ✓ 800+ Satisfied Customers • ✓ Pan-India Installation & Support
              </p>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};